import { useTranslation } from 'react-i18next';

import type { IncidentCategory, IncidentSeverity, IncidentType } from '../../../api/types';
import { Card } from '../../../shared/ui/Card';
import { Field } from '../../../shared/ui/Field';
import { Input } from '../../../shared/ui/Input';
import { Select } from '../../../shared/ui/Select';
import styles from './IncidentFilters.module.css';

const SEVERITIES: IncidentSeverity[] = ['CRITICAL', 'ERROR', 'WARNING', 'INFO'];

const CATEGORIES: IncidentCategory[] = ['INCIDENT', 'SECURITY'];

const INCIDENT_TYPES: IncidentType[] = [
  'UNHANDLED_EXCEPTION',
  'HTTP_CLIENT_ERROR',
  'EXTERNAL_SERVICE_DEGRADED',
  'BACKGROUND_JOB_FAILED',
  'NOTIFICATION_DELIVERY_FAILED',
  'STARTUP_CHECK_WARNING',
  'ADMIN_LOGIN_FAILED',
  'ADMIN_LOGIN_SUCCEEDED',
  'USER_AUTH_FAILED',
  'ADMIN_ACCESS_DENIED',
  'RATE_LIMIT_EXCEEDED',
  'WEBHOOK_SIGNATURE_INVALID',
  'PAYMENT_VALIDATION_FAILED',
  'PAYMENT_UNKNOWN_REFERENCE',
  // PARA-151, AC-8: неуспешные TOTP-верификации при логине.
  'MFA_VERIFY_FAILED',
];

interface IncidentFiltersProps {
  dateFrom: string;
  dateTo: string;
  severity: string;
  category: string;
  incidentType: string;
  acknowledged: string;
  onChange: (key: string, value: string) => void;
}

// PARA-129, AC-18: период + критичность + категория + тип события + «только
// активные». Значения живут в query-параметрах URL (ссылка шарится, как /logs).
export function IncidentFilters({
  dateFrom,
  dateTo,
  severity,
  category,
  incidentType,
  acknowledged,
  onChange,
}: IncidentFiltersProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <fieldset className={styles.filters}>
        <Field label={t('monitoring.filters.dateFrom')} htmlFor="incident-date-from">
          <Input
            id="incident-date-from"
            type="date"
            value={dateFrom}
            onChange={(event) => onChange('date_from', event.target.value)}
          />
        </Field>
        <Field label={t('monitoring.filters.dateTo')} htmlFor="incident-date-to">
          <Input
            id="incident-date-to"
            type="date"
            value={dateTo}
            onChange={(event) => onChange('date_to', event.target.value)}
          />
        </Field>
        <Field label={t('monitoring.filters.severity')} htmlFor="incident-severity">
          <Select
            id="incident-severity"
            value={severity}
            onChange={(event) => onChange('severity', event.target.value)}
          >
            <option value="">—</option>
            {SEVERITIES.map((value) => (
              <option key={value} value={value}>
                {t(`monitoring.severity.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('monitoring.filters.category')} htmlFor="incident-category">
          <Select
            id="incident-category"
            value={category}
            onChange={(event) => onChange('category', event.target.value)}
          >
            <option value="">—</option>
            {CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {t(`monitoring.category.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('monitoring.filters.incidentType')} htmlFor="incident-type">
          <Select
            id="incident-type"
            value={incidentType}
            onChange={(event) => onChange('incident_type', event.target.value)}
          >
            <option value="">—</option>
            {INCIDENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`monitoring.type.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('monitoring.filters.acknowledged')} htmlFor="incident-acknowledged">
          <Select
            id="incident-acknowledged"
            value={acknowledged}
            onChange={(event) => onChange('acknowledged', event.target.value)}
          >
            <option value="">{t('monitoring.filters.all')}</option>
            <option value="false">{t('monitoring.filters.activeOnly')}</option>
          </Select>
        </Field>
      </fieldset>
    </Card>
  );
}
